/**
 * Geteilte Datei-Helfer fuer authentifizierte Datei-Downloads (Backlog #41: R2-Original-Dateien
 * beim Speisekarten-Import + Event-Anhaenge). Gleiches Muster wie lib/print.ts
 * (openPrintableDocument): Blob-URL statt `<a href>`, weil ein simples `<a>` den Auth-Header von
 * apiFetch nicht mitschicken koennte.
 */
import { apiFetch, toApiError } from "./api";
import { oeffneImNeuenTab } from "./print";

/** Holt `path` authentifiziert und oeffnet die Antwort als Blob-URL in einem neuen Tab. */
export function openAuthenticatedFile(path: string): Promise<void> {
  // Tab synchron im Klick oeffnen (Safari-Popup-Sperre), siehe `oeffneImNeuenTab`.
  return oeffneImNeuenTab(async () => {
    const res = await apiFetch(path);
    if (!res.ok) throw await toApiError(res);
    return res.blob();
  });
}

/**
 * Holt `path` authentifiziert und legt die Antwort im Download-Ordner ab.
 *
 * Antoine am 21.09.2026 zum „Vorschau"-Knopf im Angebots-Builder: **„Anstatt Vorschau soll der
 * Button das PDF zum Download anbieten"** — und davor: **„zum download"**. Ein neuer Tab ist
 * keine Datei: was der Betreiber weiterleiten oder ablegen will, muss auf seiner Platte landen.
 *
 * Der Dateiname kommt aus dem `Content-Disposition` der Antwort — der Server weiss, wie das
 * Dokument heisst (Angebotsnummer, Kundenname). Fehlt der Kopf, greift `fallbackName`, damit im
 * Download-Ordner nie eine Datei ohne sprechenden Namen liegt.
 */
export async function ladeDateiHerunter(path: string, fallbackName: string): Promise<void> {
  const res = await apiFetch(path);
  if (!res.ok) throw await toApiError(res);
  const blob = await res.blob();
  const kopf = res.headers.get("content-disposition") ?? "";
  const treffer = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(kopf);
  const name = treffer?.[1] ? decodeURIComponent(treffer[1]) : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Die Object-URL erst nach dem Klick freigeben — sofortiges Widerrufen bricht den Download in
  // Safari ab, bevor er begonnen hat.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Datei -> {mediaType, dataBase64} (ohne data:-Praefix) fuers JSON-Body-Upload. */
/**
 * §5.8: `fileName` geht mit — ohne ihn kann eine Liste „Nicht eingelesene Dokumente" nur
 * `datei-1` anzeigen, und der Betrieb erkennt nicht, WELCHES seiner PDFs nie ankam.
 */
export async function fileToBase64(file: File): Promise<{ mediaType: string; dataBase64: string; fileName: string }> {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return { mediaType: file.type || "application/octet-stream", dataBase64: btoa(binary), fileName: file.name };
}
